import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useLocaleStore, SUPPORTED_LOCALES, DEFAULT_LOCALE } from '@/stores/locale'
import type { SupportedLocale } from '@/stores/locale'
import { ROUTES } from './routes'
import { getPropertyTypeSlug } from '@/utils/property-type-routes'
import type { ProjectType } from '@/types/enums/project-type'

/**
 * DEV-190: редирект со старого формата пагинации каталога ?page=N на
 * path-сегмент /properties/:city/page/N. page<=1 или мусор → базовый каталог
 * города без query. replace, чтобы кнопка «назад» не возвращала на legacy-URL.
 */
export function legacyPaginationGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  if (to.name === ROUTES.PROPERTIES_CATALOG_BY_CITY && to.query.page != null) {
    const raw = Array.isArray(to.query.page) ? to.query.page[0] : to.query.page
    const page = parseInt(raw ?? '', 10)
    const restQuery = { ...to.query }
    delete restQuery.page

    if (Number.isFinite(page) && page >= 2) {
      return next({
        name: ROUTES.PROPERTIES_CATALOG_BY_CITY_PAGE,
        params: { ...to.params, page: String(page) },
        query: restQuery,
        replace: true,
      })
    }
    return next({
      name: ROUTES.PROPERTIES_CATALOG_BY_CITY,
      params: { ...to.params },
      query: restQuery,
      replace: true,
    })
  }
  next()
}

/**
 * DEV-222: редирект со старого числового фильтра ?type=N на path-сегмент
 * /properties/{type-slug}. Комбо ?type=N&page=M → /properties/{type}/page/M.
 * Мусорный ?type → общий каталог без query. replace, чтобы «назад» не возвращал
 * на legacy-URL.
 */
export function legacyTypeQueryGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  if (to.name !== ROUTES.PROPERTIES_CATALOG || to.query.type == null) {
    return next()
  }

  const rawType = Array.isArray(to.query.type) ? to.query.type[0] : to.query.type
  const typeNum = parseInt(rawType ?? '', 10)
  const slug = Number.isFinite(typeNum) ? getPropertyTypeSlug(typeNum as ProjectType) : ''

  const restQuery = { ...to.query }
  delete restQuery.type

  if (!slug) {
    // Мусорный ?type — уводим на общий каталог без query.
    return next({ name: ROUTES.PROPERTIES_CATALOG, params: { ...to.params }, query: restQuery, replace: true })
  }

  const rawPage = Array.isArray(restQuery.page) ? restQuery.page[0] : restQuery.page
  const page = parseInt((rawPage as string) ?? '', 10)
  delete restQuery.page

  if (Number.isFinite(page) && page >= 2) {
    return next({
      name: ROUTES.PROPERTIES_CATALOG_BY_TYPE_PAGE,
      params: { ...to.params, type: slug, page: String(page) },
      query: restQuery,
      replace: true,
    })
  }
  return next({
    name: ROUTES.PROPERTIES_CATALOG_BY_TYPE,
    params: { ...to.params, type: slug },
    query: restQuery,
    replace: true,
  })
}

/**
 * Guard для проверки и установки локали из URL
 * Выполняется перед каждым переходом
 * Обновляет locale store (i18n синхронизируется через watch в App.vue)
 */
export function localeGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const locale = to.params.locale as string

  // Проверяем, что локаль поддерживается
  if (locale && !SUPPORTED_LOCALES.includes(locale as SupportedLocale)) {
    // Неподдерживаемая локаль - редирект на дефолтную
    const newPath = `/${DEFAULT_LOCALE}${to.path.substring(locale.length + 1)}`
    return next(newPath)
  }

  // Устанавливаем локаль в store
  // NOTE: vue-i18n синхронизируется через watcher в App.vue
  if (locale) {
    const localeStore = useLocaleStore()
    localeStore.setLocale(locale as SupportedLocale)
  } else {
    // DEV-2341: catch-all 404 объявлен вне /:locale, параметра у него нет —
    // и /en/<несуществующее> рисовался на сохранённой или русской локали.
    // Берём её из первого сегмента адреса, если он — поддерживаемая локаль.
    const segment = to.path.split('/')[1] as SupportedLocale
    if (SUPPORTED_LOCALES.includes(segment)) {
      useLocaleStore().setLocale(segment)
    }
  }

  next()
}
